import { Directive, ElementRef, OnDestroy, inject, afterNextRender } from '@angular/core';
import { Subject } from 'rxjs';
import { startWith, debounceTime, takeUntil, distinctUntilChanged } from 'rxjs/operators';

import { WindowService } from '../services/window.service';

/**
 * Directive which initially hides the DOM element and reveals it with an
 * optional animation when it gets visible to the client
 */
@Directive({ selector: '[mbRevealWhenVisible]' })
export class ElementRevealDirective implements OnDestroy {
  private element = inject(ElementRef);
  private windowService = inject(WindowService);

  // prevent animation, if the element is already visible
  private initialRun = true;

  // helper subscription which is completed when the component is destroyed
  // use this for every subscription created
  private ngUnsubscribe: Subject<boolean> = new Subject<boolean>();

  constructor() {
    afterNextRender(() => {
      // listen to scrolling events and check if item is visible
      this.windowService.windowScroll$
        .pipe(
          startWith(0), // immediately emit a scroll event
          debounceTime(20),
          takeUntil(this.ngUnsubscribe),
          distinctUntilChanged()
        )
        .subscribe(this.onWindowScroll.bind(this));
    });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

  private onWindowScroll(): void {
    if (this.isInViewPort(this.element)) {
      this.reveal();
    }
    this.initialRun = false;
  }

  /**
   * Reveals the element in the DOM
   */
  private reveal(): void {
    // immediently clear the subscription so that reveal isn't more than once
    this.ngOnDestroy();
    if (this.initialRun) {
      // do not animate if the element is already visible
      this.element.nativeElement.classList.add('initial-reveal');
    }
    this.element.nativeElement.classList.add('slide-up-animation');
  }

  /**
   * Calculate if the given element is almost visible (inside the viewport)
   *
   * @param  {ElementRef} elm
   * @return {boolean}
   */
  private isInViewPort(elm: ElementRef): boolean {
    const rect = elm.nativeElement.getBoundingClientRect();
    // also consider element beeing visible when it’s almost scrolled into view
    const tolerance = 50;
    return rect.top - tolerance <= (window.innerHeight || document.documentElement.clientHeight);
  }
}
